<template>
    <el-dialog title="分类管理" v-model="visible" width="92%" top="5vh" custom-class="category-manager-dialog"
        @closed="$emit('changed')">
        <div class="toolbar">
            <el-select v-model="appFilter" size="small" clearable placeholder="全部 App" style="width: 220px;"
                @change="load">
                <el-option v-for="a in meta.apps || []" :key="a.code" :label="a.name" :value="a.code"></el-option>
            </el-select>
            <span class="form-tip">{{ sortTip }}</span>
            <el-button type="primary" size="small" icon="el-icon-plus" @click="openForm()">新增分类</el-button>
        </div>
        <el-table ref="table" :data="list" border size="small" row-key="id" v-loading="loading"
            :row-class-name="rowClass">
            <el-table-column width="40" align="center">
                <template #default>
                    <i class="el-icon-rank drag-handle"></i>
                </template>
            </el-table-column>
            <el-table-column label="分类名称" prop="name" min-width="120"></el-table-column>
            <el-table-column label="分类标识" prop="id" min-width="110"></el-table-column>
            <el-table-column label="多语言名称" min-width="200" show-overflow-tooltip>
                <template v-slot="scope">{{ textLabel(scope.row.i18n_key) }}</template>
            </el-table-column>
            <el-table-column label="图标" prop="icon" width="100"></el-table-column>
            <el-table-column label="适用 App" min-width="130">
                <template v-slot="scope">
                    <template v-if="appNames(scope.row).length">
                        <el-tag v-for="name in appNames(scope.row)" :key="name" size="mini" type="info" class="app-tag">{{ name }}</el-tag>
                    </template>
                    <span v-else class="form-tip">全部 App</span>
                </template>
            </el-table-column>
            <el-table-column label="配网型号" prop="model_count" width="80" align="center"></el-table-column>
            <el-table-column label="操作" width="110" align="center">
                <template v-slot="scope">
                    <el-button type="text" size="small" @click="openForm(scope.row)">编辑</el-button>
                    <el-button type="text" size="small" style="color: #f56c6c;" @click="remove(scope.row)">删除</el-button>
                </template>
            </el-table-column>
        </el-table>

        <el-dialog :title="form.is_new ? '新增分类' : '编辑分类'" v-model="formVisible" width="460px" append-to-body>
            <el-form ref="formRef" :model="form" :rules="rules" label-width="110px" size="small">
                <el-form-item label="分类标识" prop="id">
                    <el-input v-model.trim="form.id" :disabled="!form.is_new" placeholder="如 indoor-cams"></el-input>
                    <div class="form-tip">App 用它分组，创建后不能修改</div>
                </el-form-item>
                <el-form-item label="分类名称" prop="name">
                    <el-input v-model="form.name" placeholder="如 Indoor Cameras"></el-input>
                </el-form-item>
                <el-form-item label="多语言名称" prop="i18n_key">
                    <el-select v-model="form.i18n_key" clearable filterable placeholder="不使用，直接显示分类名称"
                        style="width: 100%;">
                        <el-option v-for="t in textOptions" :key="t.code" :label="t.label" :value="t.code"></el-option>
                    </el-select>
                    <div class="form-tip">从文案库选择（用途：分类名），App 按语言显示
                        <el-button type="text" size="mini" @click="$emit('manage-texts', 'category')">管理文案</el-button>
                    </div>
                </el-form-item>
                <el-form-item label="图标" prop="icon">
                    <el-input v-model.trim="form.icon" placeholder="App 图标名，如 indoor-cam"></el-input>
                    <div class="form-tip">App 内置的图标名，新增图标需要 App 发版</div>
                </el-form-item>
                <el-form-item label="适用 App">
                    <el-select v-model="form.apps" multiple placeholder="留空表示所有 App 可用" style="width: 100%;">
                        <el-option v-for="a in meta.apps || []" :key="a.code" :label="a.name" :value="a.code"></el-option>
                    </el-select>
                    <div class="form-tip">留空表示所有 App 可用；只对 App 接口生效，不影响后台管理</div>
                </el-form-item>
                <MountedTexts ref="mountedTexts" v-model="form.texts" mount="category"
                    :text-types="meta.text_types || []"></MountedTexts>
            </el-form>
            <template #footer>
                <el-button size="small" @click="formVisible = false">取消</el-button>
                <el-button type="primary" size="small" @click="submit">保存</el-button>
            </template>
        </el-dialog>
    </el-dialog>
</template>

<script>
import pairingApi from './api'
import MountedTexts from './MountedTexts.vue'
import { textOptionLabel, cleanMountedTexts } from './utils'

export default {
    name: 'PairingCategoryManager',
    components: { MountedTexts },
    props: {
        meta: { type: Object, default: () => ({}) },
        // 打开时的默认 App 视图（可选）。弹窗内的 App 筛选独立于配网配置页的选择
        defaultApp: { type: String, default: '' },
    },
    emits: ['changed', 'manage-texts'],
    data() {
        return {
            visible: false,
            // 弹窗自己的 App 筛选：选了才显示该 App 的分类、才能拖动排序
            appFilter: '',
            loading: false,
            list: [],
            texts: [],
            drag: { from: -1, over: -1 },
            formVisible: false,
            form: {},
            rules: {
                id: [
                    { required: true, message: '请输入分类标识', trigger: 'blur' },
                    { pattern: /^[a-z0-9][a-z0-9-]*$/, message: '只能包含小写字母、数字和中划线', trigger: 'blur' },
                ],
                name: [{ required: true, message: '请输入分类名称', trigger: 'blur' }],
                icon: [{ required: true, message: '请输入图标名', trigger: 'blur' }],
            },
        }
    },
    computed: {
        // 当前 App 视图的显示名，未筛选时为空
        appName() {
            const app = (this.meta.apps || []).find((a) => a.code === this.appFilter)
            return this.appFilter ? (app && app.name) || this.appFilter : ''
        },
        // 排序按 App 独立，只有选定具体 App 时才能拖动
        canSort() {
            return !!this.appFilter
        },
        sortTip() {
            if (this.appFilter) {
                return `分类对应 App 手动添加页的一级列表；按住行首图标拖动，调整「${this.appName}」下的显示顺序`
            }
            return '分类对应 App 手动添加页的一级列表；排序按 App 独立，上方选择 App 后可拖动调整顺序'
        },
        // 当前值不在文案库里（如历史 key）时也保留一项，避免下拉显示成空白
        textOptions() {
            const options = this.texts.map((t) => ({ code: t.code, label: textOptionLabel(t) }))
            const current = this.form.i18n_key
            if (current && !options.some((o) => o.code === current)) {
                options.unshift({ code: current, label: `${current}（不在文案库中）` })
            }
            return options
        },
    },
    methods: {
        // 新建时默认勾选的适用 App（meta.default_app，通常是 OSAIO）
        defaultApps() {
            const code = (this.meta && this.meta.default_app) || ''
            return code ? [code] : []
        },
        // 适用 App：为空表示所有 App 可用，未在 meta 里的 code 直接显示 code
        appNames(row) {
            return (row.apps || []).map((code) => {
                const app = (this.meta.apps || []).find((a) => a.code === code)
                return (app && app.name) || code
            })
        },
        open() {
            this.visible = true
            this.appFilter = this.defaultApp || ''
            this.load()
            this.loadTexts()
        },
        loadTexts() {
            return pairingApi.TextList({ type: 'category' }).then((res) => {
                this.texts = res.data.data || []
            }).catch(() => { })
        },
        textLabel(code) {
            if (!code) return '-'
            const t = this.texts.find((x) => x.code === code)
            return t ? textOptionLabel(t) : code
        },
        load() {
            this.loading = true
            // 传 app 时后端只返回该 App 可用的分类，sort 也是该 App 的排序
            pairingApi.PairingCategoryList(this.appFilter ? { app: this.appFilter } : {}).then((res) => {
                this.loading = false
                this.list = res.data.data
                this.$nextTick(() => this.bindDrag())
            }).catch(() => {
                this.loading = false
            })
        },
        openForm(row) {
            this.form = row
                ? { is_new: false, id: row.id, name: row.name, i18n_key: row.i18n_key || '', icon: row.icon, apps: [...(row.apps || [])], texts: JSON.parse(JSON.stringify(row.texts || {})) }
                : { is_new: true, id: '', name: '', i18n_key: '', icon: '', apps: this.defaultApps(), texts: {} }
            this.formVisible = true
            this.$nextTick(() => this.$refs.formRef && this.$refs.formRef.clearValidate())
        },
        submit() {
            this.$refs.formRef.validate((valid) => {
                if (!valid) return
                const textsErr = this.$refs.mountedTexts && this.$refs.mountedTexts.validate()
                if (textsErr) {
                    this.$message.error(textsErr)
                    return
                }
                // 空数组也要提交：清空后表示所有 App 可用
                pairingApi.PairingCategorySave({
                    ...this.form,
                    apps: this.form.apps || [],
                    texts: cleanMountedTexts(this.form.texts),
                }).then((res) => {
                    if (res.data.code == 200) {
                        this.$message.success('保存成功')
                        this.formVisible = false
                        this.load()
                        return
                    }
                    this.$message.error('保存失败' + res.data.msg)
                })
            })
        },
        remove(row) {
            this.$confirm(`确定删除分类「${row.name}」吗？`, '提示', { type: 'warning' }).then(() => {
                pairingApi.PairingCategoryDelete({ id: row.id }).then((res) => {
                    if (res.data.code == 200) {
                        this.$message.success('删除成功')
                        this.load()
                        return
                    }
                    this.$message.error(res.data.msg)
                })
            }).catch(() => { })
        },
        rowClass({ rowIndex }) {
            const { from, over } = this.drag
            if (from === rowIndex) return 'is-dragging'
            if (over === rowIndex && from !== rowIndex) return 'is-drag-over'
            return ''
        },
        // 与配网方案表格相同：按住手柄拖动，事件委托在 tbody 上
        bindDrag() {
            const tbody = this.$refs.table && this.$refs.table.$el.querySelector('.el-table__body-wrapper tbody')
            if (!tbody || tbody.__dragBound) return
            tbody.__dragBound = true
            const rowIndex = (e) => {
                const tr = e.target.closest && e.target.closest('tr')
                return tr ? Array.prototype.indexOf.call(tbody.children, tr) : -1
            }
            tbody.addEventListener('mousedown', (e) => {
                const tr = e.target.closest('tr')
                // 未选 App 时不允许拖动（排序按 App 独立）
                if (tr) tr.draggable = this.canSort && !!e.target.closest('.drag-handle')
            })
            tbody.addEventListener('dragstart', (e) => {
                if (!this.canSort) {
                    e.preventDefault()
                    return
                }
                this.drag = { from: rowIndex(e), over: -1 }
                e.dataTransfer.effectAllowed = 'move'
                e.dataTransfer.setData('text/plain', '')
            })
            tbody.addEventListener('dragover', (e) => {
                e.preventDefault()
                if (this.drag.from > -1) this.drag = { ...this.drag, over: rowIndex(e) }
            })
            tbody.addEventListener('drop', (e) => {
                e.preventDefault()
                const { from, over } = this.drag
                this.drag = { from: -1, over: -1 }
                if (from < 0 || over < 0 || from === over) return
                const next = [...this.list]
                const [moved] = next.splice(from, 1)
                next.splice(over, 0, moved)
                this.list = next
                // 只有按 App 视图才能拖动，顺序只写该 App，不动基础排序
                const params = { ids: next.map((c) => c.id), app: this.appFilter }
                pairingApi.PairingCategorySort(params).then((res) => {
                    if (res.data.code == 200) {
                        this.$message.success('排序已保存（仅当前 App）')
                        return
                    }
                    this.$message.error('排序失败' + res.data.msg)
                    this.load()
                })
            })
            tbody.addEventListener('dragend', (e) => {
                this.drag = { from: -1, over: -1 }
                const tr = e.target.closest && e.target.closest('tr')
                if (tr) tr.draggable = false
            })
        },
    },
}
</script>

<style scoped>
.toolbar {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 10px;
}

.toolbar .form-tip {
    flex: 1;
    min-width: 0;
}

/* 弹窗加大后表格按容器宽度铺开，不要横向滚动 */
.category-manager-dialog ::v-deep .el-dialog__body {
    padding-top: 12px;
}

.category-manager-dialog ::v-deep .el-table {
    width: 100%;
}

.category-manager-dialog ::v-deep .el-table__body-wrapper,
.category-manager-dialog ::v-deep .el-table__header-wrapper {
    overflow-x: hidden;
}

.form-tip {
    font-size: 12px;
    line-height: 18px;
    color: #909399;
}

.drag-handle {
    color: #c0c4cc;
    cursor: move;
}

.app-tag {
    margin: 2px 4px 2px 0;
}

::v-deep(.is-dragging) {
    opacity: 0.4;
}

::v-deep(.is-drag-over td) {
    border-top: 2px solid #409eff;
}
</style>
